<template>
    <footer class="bg-gradient-to-r from-base-200 to-base-600 text-white">
        <div class="container py-5 md:py-10">
            <div class="mb-8 md:mb-10">
                <Link href="/">
                    <img
                        :src="brand.logo.url"
                        :width="brand.logo?.width > 1 ? brand.logo.width : null"
                        :height="brand.logo?.height > 1 ? brand.logo.height : null"
                        class="h-[2.2rem] hover:opacity-70 ease-out duration-300 "
                        :alt="brand.name"
                        loading="lazy"
                    >
                </Link>
            </div>

            <div class="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0 md:space-x-6 text-sm">
                <div class="w-full md:w-3/12 flex flex-col">
                    <div
                        v-if="settings.translations.footer.getInTouch"
                        class="text-xl font-bold mb-4 whitespace-nowrap"
                    >
                        {{ settings.translations.footer.getInTouch }}
                    </div>
                    <p class="mb-4">
                        {{ brand.name }}<br>
                        {{ brand.address.streetAddress }} {{ brand.address.locality }}, {{ brand.address.postalCode }},
                        {{ brand.address.country }}<br>
                        <span v-if="settings.translations.footer.ownedBy">{{ settings.translations.footer.ownedBy }}<br></span>
                    </p>

                    <a
                        v-if="brand.email"
                        :href="`mailto:${brand.email}`"
                        class="font-bold hover:opacity-70 ease-out duration-300 mb-4"
                    >
                        {{ brand.email }}
                    </a>

                    <a
                        v-if="brand.telephone"
                        :href="`tel:${brand.countryCode}${brand.telephone.replace(/\s|-/g, '').replace(/^0+/, '')}`"
                        class="phone-number font-bold hover:opacity-70 ease-out duration-300 mb-4"
                    >
                        {{ brand.telephone }}
                    </a>

                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="settings.businessHours"
                        class="mb-4"
                        v-html="settings.businessHours"
                    ></div>

                    <Link
                        v-if="settings.becomeAPartnerUrl"
                        :href="settings.becomeAPartnerUrl"
                        class="become-a-partner font-bold hover:opacity-70 ease-out duration-300 mb-4"
                    >
                        {{ settings.translations.becomePartner.title }}
                    </Link>

                    <Link
                        v-if="settings.becomeAffiliateUrl"
                        :href="settings.becomeAffiliateUrl"
                        class="become-an-affiliate font-bold hover:opacity-70 ease-out duration-300 mb-4"
                    >
                        {{ settings.translations.becomeAffiliate.title }}
                    </Link>

                    <div
                        v-if="settings.footerSuffix"
                        v-html="settings.footerSuffix"
                    ></div>
                </div>

                <div class="w-full md:w-8/12 flex flex-col sm:flex-row sm:space-x-4">
                    <div class="flex flex-col w-full sm:w-1/2">
                        <div class="text-xl font-bold mb-4 whitespace-nowrap">
                            {{ footer1Menu.name?.replace('_', '') }}
                        </div>

                        <Link
                            v-for="item in footer1Menu.items"
                            :key="item.url"
                            :href="item.url"
                            class="hover:opacity-70 ease-out duration-300 mb-4"
                        >
                            <span v-html="item.name"></span>
                        </Link>
                    </div>

                    <div class="flex flex-col w-full sm:w-1/2">
                        <div class="text-xl font-bold mb-4 whitespace-nowrap">
                            {{ footer2Menu.name?.replace('_', '') }}
                        </div>

                        <Link
                            v-for="item in footer2Menu.items"
                            :key="item.url"
                            :href="item.url"
                            class="hover:opacity-70 ease-out duration-300 mb-4"
                        >
                            <span v-html="item.name"></span>
                        </Link>
                    </div>

                    <template v-if="footer3Menu?.items?.length > 0">
                        <div class="flex flex-col w-full sm:w-1/2">
                            <div class="text-xl font-bold mb-4 whitespace-nowrap">
                                {{ footer3Menu.name?.replace('_', '') }}
                            </div>

                            <Link
                                v-for="item in footer3Menu.items"
                                :key="item.url"
                                :href="item.url"
                                class="hover:opacity-70 ease-out duration-300 mb-4"
                            >
                                <span v-html="item.name"></span>
                            </Link>
                        </div>
                    </template>
                </div>
                <template v-if="footer3Menu?.items?.length === 0">
                    <div class="w-full md:w-1/12 flex md:flex-col space-x-3 md:space-x-0 md:space-y-3">
                        <a
                            v-if="settings.facebookUrl"
                            ref="noopener noreferrer"
                            :href="settings.facebookUrl"
                            target="_blank"
                            class="w-fit"
                        >
                            <span class="button-facebook-light">Facebook</span>
                        </a>
                        <a
                            v-if="settings.twitterUrl"
                            ref="noopener noreferrer"
                            :href="settings.twitterUrl"
                            target="_blank"
                            class="w-fit"
                        >
                            <span class="button-twitter-light">Twitter</span>
                        </a>
                        <a
                            v-if="settings.linkedinUrl"
                            ref="noopener noreferrer"
                            :href="settings.linkedinUrl"
                            target="_blank"
                            class="w-fit"
                        >
                            <span class="button-linkedin-light">LinkedIn</span>
                        </a>
                        <a
                            v-if="settings.youtubeUrl"
                            ref="noopener noreferrer"
                            :href="settings.youtubeUrl"
                            target="_blank"
                            class="w-fit"
                        >
                            <span class="button-youtube-light">Youtube</span>
                        </a>
                        <a
                            v-if="settings.pinterestUrl"
                            ref="noopener noreferrer"
                            :href="settings.pinterestUrl"
                            target="_blank"
                            class="w-fit"
                        >
                            <span class="button-pinterest-light">Pinterest</span>
                        </a>
                    </div>
                </template>
            </div>
        </div>
        <div class="flex flex-col bg-gradient-to-r from-black/20 to-black/10 py-5 md:py-10">
            <div class="container">
                <div
                    v-if="settings.footerBadges.length > 0"
                    class="flex justify-center items-center w-full mb-6"
                >
                    <div class="border-b border-white w-full mr-6 opacity-50"></div>
                    <div class="flex flex-col items-center justify-center xs:flex-row w-full space-y-4 xs:space-y-0 xs:space-x-4">
                        <div
                            v-for="badge in settings.footerBadges"
                            :key="badge.url"
                        >
                            <img
                                class="object-contain max-w-max"
                                :src="badge.url"
                                :alt="badge.alt"
                                loading="lazy"
                            >
                        </div>
                    </div>
                    <div class="border-b border-white w-full ml-6 opacity-50"></div>
                </div>

                <!-- eslint-disable vue/no-v-html -->
                <div
                    class="disclaimer text-white/70 text-sm text-center mb-10"
                    v-html="settings.translations.footer.disclaimer ?? ''"
                ></div>
            </div>

            <CookieConsent v-if="!settings.oneTrustEnabled" />
        </div>
    </footer>
</template>

<script setup>
import { usePage } from '@inertiajs/vue3';
import CookieConsent from './CookieConsent.vue';

const page = usePage();

const brand = page.props.brand;
const footer1Menu = page.props.menus?.footer1Menu || { items: [], name: '' };
const footer2Menu = page.props.menus?.footer2Menu || { items: [], name: '' };
const footer3Menu = page.props.menus?.footer3Menu || { items: [], name: '' };
const settings = page.props.settings;
</script>

<style scoped>
.disclaimer :deep(a) {
  @apply underline hover:text-white/80 ease-out duration-300;
}
</style>
